/**
 * Notes on clipboard copying
 * navigator.clipboard.writeText is the modern API
 * document.execCommand is the legacy (not supported in Safari)
 * https://developer.mozilla.org/en-US/docs/Mozilla/Add-ons/WebExtensions/Interact_with_the_clipboard
 */

/**
 * **createHiddenTextArea**_(input)_<br>
 * Creates a hidden textarea.
 * @param { String } input: The value to add to the text area.
 * @returns { Object } The hidden text area.
 */
export const createHiddenTextArea = (input) => {
  const el = document.createElement('textarea');
  if (input) {
    el.setAttribute('readonly', '');
    el.value = input;
  }
  el.style.height = '0px';
  el.style.left = '-9999px';
  el.style.opacity = 0;
  el.style.overflow = 'hidden';
  el.style.position = 'absolute';
  el.style.width = '0px';
  return el;
};

/**
 * **copyToClipboard**_(input)_<br>
 * Copies an input value to the clipboard.
 * @param { String } input: The value to copy to clipboard.
 * @returns { Boolean } Value copied successfully.
 */
export const copyToClipboard = (input) => {
  const el = createHiddenTextArea(input);
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
  return true;
};

export default copyToClipboard;

/**
 * This file should run once when the JS loads and then preserve
 * the permissions state for clipboard access throughout the session
 * so that we don't have to constantly requery to check access
 */

// annoying workaround for Jest tests not allowing mocking of navigator
const supportsNavigation =
  !!window.navigator && !!window.navigator.permissions && !!window.navigator.permissions.query;

const supportsClipboard =
  supportsNavigation &&
  window.navigator.permissions.query({ name: 'clipboard-write' }).then((result) => {
    const allowedResults = ['granted', 'prompt'];
    return allowedResults.includes(result.state);
  });

/**
 * Attempt to copy to the clipboard using modern API
 * fall back to legacy implementation if modern API unsupported
 * @returns {Promise}
 */
export const updateClipboard = (text) => {
  if (supportsClipboard) {
    return window.navigator.clipboard.writeText(text);
  }

  return new Promise((res, rej) => {
    try {
      const result = copyToClipboard(text);
      res(result);
    } catch (e) {
      rej(e);
    }
  });
};
